import {
  CircularProgress,
  Dialog,
  DialogBody,
  DialogContent,
  DialogTrigger,
  IconButton,
  Tooltip,
} from '@civilgrid/ui/components';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Document, Page } from 'react-pdf';
import '~/common/pdfWorker';
import type { CivilGridFile } from '~/common/types';

type FileType = 'image' | 'video' | 'pdf';

interface Props {
  file: CivilGridFile;
}

export default function FileViewer({ file }: Props) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const fileType = useMemo<FileType | undefined>(() => {
    if (file.type === 'application/pdf') {
      return 'pdf';
    } else if (file.type.startsWith('image/')) {
      return 'image';
    } else if (file.type.startsWith('video/')) {
      return 'video';
    }
  }, [file.id]);

  if (!fileType) return null;

  return (
    <Dialog>
      <Tooltip text="View file" asChild>
        <DialogTrigger asChild>
          <IconButton
            variant="hollow"
            icon="eyeOpen"
            aria-label={`View file ${file.name}`}
            data-testid={`file-viewer-open-${file.id}`}
          />
        </DialogTrigger>
      </Tooltip>
      <DialogContent
        title={file.name || 'File preview'}
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid={`file-viewer-dialog-${file.id}`}
      >
        <DialogBody>
          <Content file={file} fileType={fileType} />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

const Content = ({ file, fileType }: Props & { fileType: FileType }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  useEffect(() => {
    setIsLoading(true);
  }, []);

  if (fileType === 'pdf') {
    return (
      <Container isLoading={isLoading}>
        <div className="flex flex-col items-center justify-between overflow-clip">
          <div className="flex items-center space-x-2">
            <IconButton
              icon="arrowLeft"
              variant="hollow"
              aria-label="Previous page"
              data-testid="file-viewer-previous-page"
              onClick={() => {
                setPage((prev) => (!(prev - 1) ? prev : prev - 1));
              }}
            />

            <div>
              {page} / {pageCount}
            </div>

            <IconButton
              icon="arrowRight"
              variant="hollow"
              aria-label="Next page"
              data-testid="file-viewer-next-page"
              onClick={() => {
                setPage((prev) => (prev + 1 > pageCount ? prev : prev + 1));
              }}
            />
          </div>

          <Document
            file={file.path}
            onLoadSuccess={(pdf) => {
              setPageCount(pdf.numPages);
              setIsLoading(false);
            }}
          >
            <Page pageNumber={page} renderTextLayer={false} />
          </Document>
        </div>
      </Container>
    );
  }

  if (fileType === 'video') {
    return (
      <Container isLoading={isLoading}>
        <video src={file.path} className="max-h-[90vh] max-w-[90vw]" controls onLoadedData={() => setIsLoading(false)}>
          <track kind="captions" />
        </video>
      </Container>
    );
  }

  return (
    <Container isLoading={isLoading}>
      <img
        src={file.path}
        alt="Supporting file attachment"
        className="max-h-[90vh] max-w-[90vw]"
        onLoad={() => setIsLoading(false)}
      />
    </Container>
  );
};

const Container = ({ children, isLoading }: { children?: React.ReactNode; isLoading: boolean }) => {
  return (
    <div className="relative flex w-full justify-center overflow-auto">
      {children}
      {isLoading && (
        <div className="absolute left-0 top-0 flex h-full w-full grow items-center justify-center bg-white bg-opacity-50">
          <CircularProgress indeterminate />
        </div>
      )}
    </div>
  );
};
